import React, { Fragment, useEffect } from 'react'
import { objectEntries } from 'ts-extras'
import { Actions } from '~/actions'
import { Button } from '~/components/Button'
import { useTranslations } from '~/components/Locale'
import { Image } from '~/components/Photos'
import { Price } from '~/components/utils'
import type { ComparisonSection as ComparisonSectionSettings, HTMLElementProps } from '~/types'
import { cn, includes, useDispatch, useSelector } from '~/utils'
import { Section, Title } from './Common'

interface Props {
	section: ComparisonSectionSettings
}

export const Checkbox: React.FC<HTMLElementProps<'svg'>> = props => (
	<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20" {...props}>
		<path fill="currentColor" d="M10 20a10 10 0 1 1 0-20 10 10 0 0 1 0 20Z" />
		<path
			fill="#fff"
			d="M8.7 14.3c-.3.2-.6.2-.9 0L4 10.4a.6.6 0 0 1 0-.8l.4-.4c.2-.2.6-.2.8 0l2.6 2.6c.3.3.6.3.9 0l6-6c.3-.3.7-.3 1 0l.3.3c.3.2.3.6 0 .8l-7.3 7.4Z"
		/>
	</svg>
)

const Badge: React.FC<HTMLElementProps<'span'>> = ({ className, children, ...props }) => (
	<span
		className={cn('rounded-xl bg-[#B2E2C8] px-4 py-1.5 text-sm font-semibold text-green-700', className)}
		{...props}
	>
		{children}
	</span>
)

export const ComparisonSection: React.FC<Props> = ({
	section: {
		tag,
		settings: { product_a, product_b, ...settings },
		blocks
	}
}) => {
	const products = useSelector(state => state.products.products)
	const dispatch = useDispatch()
	useEffect(() => {
		if (!products) dispatch(Actions.loadProducts())
	}, [products, dispatch])

	const t = useTranslations('react')
	return (
		<Section
			className="space-y-20"
			tag={tag}
			style={{
				paddingTop: `${settings.size_top}rem`,
				paddingBottom: `${settings.size_bottom}rem`,
				backgroundColor: settings.background_color,
				color: settings.text_color
			}}
		>
			<Title className="max-sm:text-center">{settings.title}</Title>
			<div className="text-16 self-start sm:row-start-2" dangerouslySetInnerHTML={{ __html: settings.text }} />
			<div className="grid grid-flow-col grid-cols-3 items-center justify-center gap-y-5 text-center sm:gap-x-20">
				{objectEntries({ product_a, product_b }).map(([key, handle], i) => {
					const product = products?.find(p => p.handle === handle)
					if (!product) return null
					const info = {
						title: settings[`${key}_title`] || product.title,
						subtitle: settings[`${key}_subtitle`],
						label: settings[`${key}_label`],
						image: settings[`${key}_image`] || product.featuredImage,
						titleLabel: settings[`${key}_title_label`]
					}
					return (
						<div
							key={i}
							className={cn('relative row-start-1 mb-4 space-y-2', {
								'col-start-1 col-end-3 max-sm:mr-[30%] sm:col-start-2': i === 0,
								'col-start-2 col-end-4 max-sm:ml-[30%] sm:col-start-3': i === 1
							})}
							role="columnheader"
							aria-rowindex={i + 2}
						>
							{info.label && (
								<div className="absolute -top-2 flex w-full items-center justify-center">
									<Badge>{info.label}</Badge>
								</div>
							)}
							{info.image && (
								<Image className="aspect-[11/9] w-full rounded-3xl object-cover" src={info.image} />
							)}
							<div className="flex items-center justify-center space-x-3">
								<div className="sm:text-24 text-2xl font-bold">{info.title}</div>
								{info.titleLabel && <Badge>{info.titleLabel}</Badge>}
							</div>
							<h4 className="sm:text-18 text-lg font-bold">{info.subtitle}</h4>
							<p className="sm:text-16 text-base italic">
								{t?.checkout.from} <Price price={product.priceRange.minVariantPrice} asText />
							</p>
						</div>
					)
				})}
				{blocks.map((block, y) => (
					<Fragment key={block.id}>
						{'title' in block.settings && (
							<>
								{y > 0 && (
									<hr
										className="bg-gray-150 col-span-full my-0 h-px"
										style={{ gridRow: y * 2 + 1 }}
									/>
								)}
								<h4
									className="col-start-2 m-0 h-full text-xl italic sm:col-start-1 sm:text-left"
									style={{ gridRow: y * 2 + 2 }}
									aria-colindex={1}
									aria-rowindex={y + 1}
								>
									{block.settings.title}
								</h4>
							</>
						)}

						{objectEntries({ option_a: block.settings.option_a, option_b: block.settings.option_b }).map(
							([key, option], x) => (
								<div
									key={key}
									className={cn('flex flex-col items-center justify-center space-y-4', {
										'col-start-1 sm:col-start-2': x === 0,
										'col-start-3 sm:col-start-3': x === 1,
										'max-sm:col-start-1 max-sm:col-end-3 max-sm:mr-[30%]':
											x === 0 && includes(block.type, 'comparison_image', 'comparison_cta'),
										'max-sm:col-start-2 max-sm:col-end-4 max-sm:ml-[30%]':
											x === 1 && includes(block.type, 'comparison_image', 'comparison_cta'),
										'max-sm:mt-8': block.type === 'comparison_image'
									})}
									style={{ gridRow: y * 2 + (block.type === 'comparison_cta' ? 1 : 2) }}
									aria-rowindex={y + 1}
									aria-colindex={x + 2}
									role="cell"
								>
									{typeof option === 'boolean' ? (
										option ? (
											<Checkbox className="h-7 text-green-700" aria-label="Yes" />
										) : (
											<span>No</span>
										)
									) : block.type === 'comparison_image' ? (
										<>
											<div className="w-44 text-base font-semibold text-green-700">{option}</div>
											<Image
												className="aspect-[11/9] w-full rounded-3xl object-cover"
												src={block.settings[`${key}_image`]}
											/>
										</>
									) : block.type === 'comparison_cta' ? (
										<Button
											className="rounded-full bg-[#3FAF6D] text-base font-semibold"
											href={
												option ||
												`/products/${key.endsWith('a') ? product_a : product_b}` ||
												undefined
											}
											size="md"
										>
											{t?.actions.buy_now}
										</Button>
									) : (
										<span className="rounded-full bg-green-700 px-6 py-0.5 text-base font-semibold text-white">
											{option}
										</span>
									)}
								</div>
							)
						)}
					</Fragment>
				))}
			</div>
		</Section>
	)
}
